import { left, right } from 'fp-ts/Either';
import { FIELD_TYPE } from 'domain/entity/preset/field/fieldType';
import { httpClientFunction } from 'domain/types';
import FieldValue from 'domain/entity/preset/field/fieldValue';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import ReadPresetField from 'use-case/presets/fields/read/readPresetField';
import ReadPresetFieldCriteria, { PresetFieldDependency } from 'use-case/presets/fields/read/readPresetFieldCriteria';
import { PresetFieldResult } from 'use-case/presets/fields/read/readPresetFieldUseCase';
import PresetField from 'domain/entity/preset/field/presetField';
import { isRight } from 'fp-ts/lib/These';

export default (httpClient: httpClientFunction, baseUrl: string): ReadPresetField => (
    criteria: ReadPresetFieldCriteria
): Promise<PresetFieldResult> =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve) => {
        const queryParams = criteria.dependencies && criteria.dependencies.length > 0 ? `&${asQueryParams(criteria.dependencies)}` : '';
        let newPresetField = new PresetField('', []);

        let thereAreMoreValuesToRetrieve = true;

        while (thereAreMoreValuesToRetrieve) {
            // eslint-disable-next-line no-await-in-loop
            const result = await httpClient(
                `${baseUrl}/dashboards/presets/${criteria.preset}/fields/${criteria.field}?widget_type=${
                    criteria.widgetType
                }${queryParams}${criteria.pageSize > 0 ? `&start=${newPresetField.values.length}&size=${criteria.pageSize}` : ''}`,
                {}
            )
                .then((response) =>
                    response.ok
                        ? response
                              .json()
                              .then(asPresetField)
                              .then(right)
                              .catch(() => left({ error: new Error(), status: 500 }))
                        : left({ error: new Error(), status: response.status })
                )
                .catch(() => left({ error: new Error(), status: 503 }));

            if (isRight(result)) {
                const presetField = result.right;
                if (criteria.pageSize === 0 || presetField.values.length < criteria.pageSize) thereAreMoreValuesToRetrieve = false;
                newPresetField = new PresetField(
                    presetField.type,
                    [...newPresetField.values, ...presetField.values],
                    presetField.maxSelectionLength,
                    presetField.minSelectionLength
                );
            } else {
                return resolve(result);
            }
        }

        return resolve(right(newPresetField));
    });
const asQueryParams = (dependencies: PresetFieldDependency[]) =>
    dependencies.map((dependency) => `${dependency.key}=${dependency.value}`).join('&');

const asPresetField = (presetField: PresetFieldJson): PresetField =>
    new PresetField(presetField.type, asFieldsValues(presetField), presetField.max_selection_length, presetField.min_selection_length);

const asFieldsValues = (preset: PresetFieldJson): FieldValue[] => {
    switch (preset.type) {
        case FIELD_TYPE.PERIOD_SELECTOR:
        case FIELD_TYPE.SELECTOR:
        case FIELD_TYPE.TREE_SELECTOR:
            return asSelectorsValues(preset.values);
        default:
            throw Error(`Not implemented preset type ${preset.type}`);
    }
};

const asSelectorsValues = (selectorValuesJson: Array<selectorValueJson>): SelectorValue[] =>
    selectorValuesJson.map(
        (selectorValueItem: selectorValueJson) =>
            new SelectorValue(
                selectorValueItem.label,
                selectorValueItem.id,
                selectorValueItem.selected,
                selectorValueItem.properties
                    ? {
                          icon: selectorValueItem.properties.icon,
                          shortLabel: selectorValueItem.properties.short_label,
                          isDisabled: selectorValueItem.properties.is_disabled,
                          disabled: selectorValueItem.properties.disabled,
                      }
                    : {},
                selectorValueItem.children ? asSelectorsValues(selectorValueItem.children) : undefined,
                selectorValueItem.selectable,
                selectorValueItem.disabled
            )
    );

type fieldValueJson = {
    label: string;
    id: string;
};

type selectorPropertiesJson = {
    icon?: string;
    ['short_label']?: string;
    ['is_disabled']?: boolean;
    disabled?: boolean;
};

type selectorValueJson = fieldValueJson & {
    selected: boolean;
    selectable?: boolean;
    disabled?: boolean;
    properties?: selectorPropertiesJson | Record<string, never>;
    children?: selectorValueJson[];
};

type FieldsValueJson = selectorValueJson;

type PresetFieldJson = {
    type: string;
    values: FieldsValueJson[];
    ['max_selection_length']?: number;
    ['min_selection_length']?: number;
};
